import { sileo } from "sileo"
import { ReactNode } from "react"

type SileoPosition = "top-left" | "top-center" | "top-right" | "bottom-left" | "bottom-center" | "bottom-right"

type SileoStyles = {
  title?: string
  description?: string
  badge?: string
  button?: string
}

type ToastOptions = {
  description?: ReactNode | string
  position?: SileoPosition
  duration?: number | null
  icon?: ReactNode | null
  fill?: string
  styles?: SileoStyles
  roundness?: number
  autopilot?: boolean | { expand: number; collapse: number }
}

type ActionOptions = ToastOptions & {
  button?: { title: string; onClick: () => void }
}

type SileoOptions = ToastOptions & {
  title: string
}

type PromiseStates<T> = {
  loading: SileoOptions
  success: SileoOptions | ((data: T) => SileoOptions)
  error: SileoOptions | ((err: unknown) => SileoOptions)
  action?: SileoOptions | ((data: T) => SileoOptions)
  position?: SileoPosition
}

const defaultStyles: SileoStyles = {
  title: "text-background! normal-case!",
  description: "text-background/75!",
}

const withStyles = (options?: ToastOptions) => ({
  ...options,
  styles: { ...defaultStyles, ...options?.styles },
})

const fitsTitle = (title: string) => {
  if (typeof document === 'undefined') return true
  const context = document.createElement('canvas').getContext('2d')
  if (!context) return true
  context.font = `500 13.2px ${getComputedStyle(document.body).fontFamily}`
  return context.measureText(title).width <= Math.min(292, window.innerWidth - 82) * 1.75
}

const withTitle = (title: string, label: string, options?: ToastOptions) =>
  options?.description || fitsTitle(title)
    ? { title, ...withStyles(options) }
    : { title: label, ...withStyles({ ...options, description: title }) }

const toast = {
  success: (title: string, options?: ToastOptions) =>
    sileo.success(withTitle(title, 'Амжилттай', options)),
  error: (title: string, options?: ToastOptions) =>
    sileo.error(withTitle(title, 'Алдаа гарлаа', options)),
  info: (title: string, options?: ToastOptions) =>
    sileo.info(withTitle(title, 'Мэдээлэл', options)),
  warning: (title: string, options?: ToastOptions) =>
    sileo.warning(withTitle(title, 'Анхааруулга', options)),
  loading: (title: string, options?: ToastOptions) =>
    sileo.show({ title, type: "loading", duration: null, ...withStyles(options) }),
  action: (title: string, options?: ActionOptions) =>
    sileo.action({ title, ...withStyles(options) }),
  show: (title: string, options?: ToastOptions) =>
    sileo.show({ title, ...withStyles(options) }),
  promise: <T>(promise: Promise<T>, states: PromiseStates<T>) =>
    sileo.promise(promise, {
      ...states,
      loading: withStyles(states.loading),
      success: typeof states.success === 'function'
        ? (data: T) => withStyles((states.success as (d: T) => SileoOptions)(data))
        : withStyles(states.success),
      error: typeof states.error === 'function'
        ? (err: unknown) => withStyles((states.error as (e: unknown) => SileoOptions)(err))
        : withStyles(states.error),
    }),
  dismiss: (id: string) => sileo.dismiss(id),
  clear: (position?: SileoPosition) => sileo.clear(position),
}

export { toast }